import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";

interface Station {
  id: string;
  code: string;
  type: string;
  position_description: string | null;
  location_id: string;
  location_name: string;
  location_address: string;
  client_name: string;
}

interface Job {
  id: string;
  scheduled_at: string;
  status: string;
  location_name: string;
}

const findingsOptions = ["none", "low_activity", "moderate_activity", "high_activity"] as const;
const pestTypeOptions = ["rodent", "cockroach", "ant", "fly", "stored_product_pest", "other"] as const;
const baitStatusOptions = ["intact", "partially_consumed", "fully_consumed", "replaced"] as const;

export function InspectionForm() {
  const { t } = useTranslation();
  const { stationId } = useParams<{ stationId: string }>();
  const [station, setStation] = useState<Station | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    jobId: "",
    findings: "none",
    pestType: "",
    actionsTaken: "",
    baitStatus: "intact",
    notes: "",
  });

  useEffect(() => {
    async function load() {
      try {
        const s = await api(`/stations/${stationId}`);
        setStation(s);

        // Find active or upcoming jobs for this station's location
        const allJobs = await api(`/jobs?locationId=${s.location_id}`);
        const activeJobs = allJobs
          .filter((j: Job) => j.status === "in_progress" || j.status === "scheduled")
          .sort((a: Job, b: Job) =>
            a.status === "in_progress" ? -1 : b.status === "in_progress" ? 1 :
            new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
          );
        setJobs(activeJobs);

        if (activeJobs.length > 0) {
          setForm((f) => ({ ...f, jobId: activeJobs[0].id }));
        }
      } catch {
        // handled by empty state
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [stationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api("/inspections", {
      method: "POST",
      body: JSON.stringify({
        jobId: form.jobId,
        stationId,
        findings: form.findings,
        pestType: form.pestType || null,
        actionsTaken: form.actionsTaken || null,
        baitStatus: station?.type === "bait_station" ? form.baitStatus : null,
        notes: form.notes || null,
      }),
    });
    setSubmitted(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-navy-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!station) {
    return (
      <div className="animate-fade-in text-center py-20">
        <p className="text-navy-500">{t("common.error")}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="animate-fade-in max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-navy-100 p-8 text-center">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold mb-1" style={{ fontFamily: "var(--font-display)" }}>
            {t("inspections.inspectionSaved")}
          </h2>
          <p className="text-sm text-navy-400 mb-6">
            {station.code} — {t(`locations.stationTypes.${station.type}`)}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setSubmitted(false); setForm({ ...form, findings: "none", pestType: "", actionsTaken: "", baitStatus: "intact", notes: "" }); }} className="btn btn-primary">
              {t("inspections.inspectAnother")}
            </button>
            <Link to={`/app/clients`} className="btn btn-ghost">
              {t("common.back")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      {/* Station header */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-navy-900 rounded-xl flex items-center justify-center">
            <span className="text-amber-400 font-mono text-lg font-bold">{station.code}</span>
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ fontFamily: "var(--font-display)" }}>
              {t(`locations.stationTypes.${station.type}`)}
            </h1>
            <p className="text-sm text-navy-400">
              {station.location_name} — {station.client_name}
            </p>
            {station.position_description && (
              <p className="text-xs text-navy-300 mt-0.5">{station.position_description}</p>
            )}
          </div>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-8 text-center">
          <p className="text-navy-600 font-medium mb-1">{t("inspections.noActiveJob")}</p>
          <p className="text-sm text-navy-400 mb-4">{t("inspections.noActiveJobDesc")}</p>
          <Link to="/app/schedule" className="btn btn-primary btn-sm">
            {t("jobs.addJob")}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-navy-100 p-6">
          <h2 className="font-semibold mb-5" style={{ fontFamily: "var(--font-display)" }}>
            {t("inspections.record")}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Job selector */}
            {jobs.length > 1 && (
              <div className="sm:col-span-2">
                <label className="input-label">{t("inspections.job")}</label>
                <select value={form.jobId} onChange={(e) => setForm({ ...form, jobId: e.target.value })} className="input">
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>
                      {new Date(j.scheduled_at).toLocaleDateString()} — {t(`jobs.status.${j.status}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Findings */}
            <div>
              <label className="input-label">{t("haccp.findings")} *</label>
              <select value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} className="input">
                {findingsOptions.map((f) => (
                  <option key={f} value={f}>{t(`inspections.findings.${f}`)}</option>
                ))}
              </select>
            </div>

            {/* Pest type */}
            <div>
              <label className="input-label">{t("haccp.pest")}</label>
              <select value={form.pestType} onChange={(e) => setForm({ ...form, pestType: e.target.value })} className="input">
                <option value="">{t("inspections.selectPestType")}</option>
                {pestTypeOptions.map((p) => (
                  <option key={p} value={p}>{t(`inspections.pestTypes.${p}`)}</option>
                ))}
              </select>
            </div>

            {/* Bait status — only for bait stations */}
            {station.type === "bait_station" && (
              <div>
                <label className="input-label">{t("inspections.baitStatus.label")}</label>
                <select value={form.baitStatus} onChange={(e) => setForm({ ...form, baitStatus: e.target.value })} className="input">
                  {baitStatusOptions.map((b) => (
                    <option key={b} value={b}>{t(`inspections.baitStatus.${b}`)}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Actions taken */}
          <div className="mb-4">
            <label className="input-label">{t("inspections.actionsTaken")}</label>
            <textarea value={form.actionsTaken} onChange={(e) => setForm({ ...form, actionsTaken: e.target.value })} rows={2} className="input" style={{ resize: "vertical" }} />
          </div>

          {/* Notes */}
          <div className="mb-5">
            <label className="input-label">{t("inspections.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input" style={{ resize: "vertical" }} />
          </div>

          <button type="submit" className="btn btn-primary w-full">
            {t("inspections.submitInspection")}
          </button>
        </form>
      )}
    </div>
  );
}
